import { useEffect, useState } from 'react';
import { projectsApi } from '@/api/projects.api';
import { CustomerSelect, ProjectSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { ProjectTemplate } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProjectsSetupPage() {
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [portalForm, setPortalForm] = useState({ projectId: '', customerId: '', email: '' });
  const [docProjectId, setDocProjectId] = useState('');
  const [documents, setDocuments] = useState<{ id: number; fileName: string; fileUrl: string }[]>([]);

  useEffect(() => {
    projectsApi.listTemplates().then(setTemplates).catch((e) => showApiError(e, 'Failed to load templates'));
  }, []);

  useEffect(() => {
    if (!docProjectId) { setDocuments([]); return; }
    projectsApi.listProjectDocuments(Number(docProjectId)).then(setDocuments).catch(() => setDocuments([]));
  }, [docProjectId]);

  const uploadDoc = async (file: File) => {
    if (!docProjectId) return;
    try {
      await projectsApi.uploadProjectDocument(Number(docProjectId), file);
      notify.success('Document uploaded');
      setDocuments(await projectsApi.listProjectDocuments(Number(docProjectId)));
    } catch (err) {
      showApiError(err, 'Upload failed');
    }
  };

  const instantiate = async (code: string) => {
    const projectCode = `${code}-${Date.now()}`.slice(0, 20);
    try {
      await projectsApi.instantiateTemplate({ templateCode: code, projectCode, projectName: `From ${code} template` });
      notify.success('Project created from template');
    } catch (err) {
      showApiError(err, 'Instantiate failed');
    }
  };

  const inviteClient = async () => {
    try {
      await projectsApi.createPortalUser({
        projectId: Number(portalForm.projectId),
        customerId: Number(portalForm.customerId),
        email: portalForm.email,
      });
      notify.success('Client portal user created');
    } catch (err) {
      showApiError(err, 'Portal invite failed');
    }
  };

  return (
    <div>
      <PageHeader title="Project Setup" description="Templates, client portal, and configuration"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Setup' }]} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="font-semibold mb-3">Project templates</h3>
          <ul className="space-y-2">
            {templates.map((t) => (
              <li key={t.id} className="flex justify-between items-center border-b pb-2">
                <span>{t.templateName} <span className="text-xs text-slate-500">({t.templateCode})</span></span>
                <Button variant="secondary" onClick={() => void instantiate(t.templateCode)}>Use</Button>
              </li>
            ))}
          </ul>
        </div>
        <div className="card p-4 space-y-3">
          <h3 className="font-semibold">Client portal access</h3>
          <ProjectSelect
            value={portalForm.projectId}
            onChange={(v) => setPortalForm({ ...portalForm, projectId: v })}
          />
          <CustomerSelect
            value={portalForm.customerId}
            onChange={(v) => setPortalForm({ ...portalForm, customerId: v })}
          />
          <Input label="Client email" type="email" value={portalForm.email} onChange={(e) => setPortalForm({ ...portalForm, email: e.target.value })} />
          <Button onClick={() => void inviteClient()}>Grant portal access</Button>
        </div>
        <div className="card p-4 space-y-3 lg:col-span-2">
          <h3 className="font-semibold">Project documents</h3>
          <ProjectSelect value={docProjectId} onChange={setDocProjectId} />
          <input type="file" className="text-sm" onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadDoc(f); }} />
          <ul className="text-sm space-y-1">{documents.map((d) => <li key={d.id}><a href={d.fileUrl} className="text-primary-600 hover:underline" target="_blank" rel="noreferrer">{d.fileName}</a></li>)}</ul>
        </div>
      </div>
    </div>
  );
}
